import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { MeetingLiveShare, FollowModeState, ViewState } from '@annoteer/platform-adapter';
// Narrow subpaths, not the package barrel. The barrel re-exports
// TeamsLiveShareBridge, which statically imports @microsoft/live-share --
// so a single eager `import { … } from '@annoteer/platform-adapter'`
// anywhere put 857 kB of Fluid in the first paint, and defeated the
// careful `await import()` calls below. Rollup says so out loud:
// "dynamically imported ... but also statically imported ... dynamic
// import will not move module into another chunk".
import { DevLiveShareBridge } from '@annoteer/platform-adapter/DevLiveShareBridge';
import { usePlatform } from './PlatformContext.js';

interface LiveShareContextValue {
  /** Whether the Live Share bridge is initialized and ready. */
  isReady: boolean;
  /** Current follow mode state, or null if not ready. */
  followModeState: FollowModeState | null;
  /** True when the local user is actively presenting. */
  isPresenting: boolean;
  /** True when the local user is following a presenter. */
  isFollowing: boolean;
  /** True when the local user has navigated away from the presenter's view. */
  isSuspended: boolean;
  /** Diagnostic: live stateChanged events received since mount. See the
   *  subscription in this file for what a zero here rules out. */
  eventCount: number;
  /** Start presenting — followers will see this user's view state. */
  startPresenting: () => Promise<void>;
  /** Stop presenting. */
  stopPresenting: () => Promise<void>;
  /** Update the local user's current view state (document + page). */
  updateViewState: (state: ViewState) => Promise<void>;
  /** Temporarily suspend following (user navigated away). */
  beginSuspension: () => Promise<void>;
  /** Resume following the presenter. */
  endSuspension: () => Promise<void>;
}

const LiveShareCtx = createContext<LiveShareContextValue | null>(null);

const noop = async () => {};

export function LiveShareProvider({ children }: { children: ReactNode }) {
  const { context } = usePlatform();
  const bridgeRef = useRef<MeetingLiveShare | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [followModeState, setFollowModeState] = useState<FollowModeState | null>(null);
  const [eventCount, setEventCount] = useState(0);

  // Initialize bridge based on whether we're in a Teams meeting
  useEffect(() => {
    if (!context) return;

    let disposed = false;
    let unsubscribe: (() => void) | null = null;

    async function init() {
      let bridge: MeetingLiveShare;

      if (context!.meetingId) {
        // Real Teams meeting — lazy-load Live Share SDK and use real bridge.
        // Three-tier cascade:
        //   1. LiveShareHost.create() from @microsoft/teams-js — real Teams Fluid relay
        //   2. TestLiveShareHost.create() from @microsoft/live-share — local test Fluid
        //   3. DevLiveShareBridge — in-memory fallback (no cross-user sync)
        try {
          const teamsJs = await import('@microsoft/teams-js');
          const { TeamsLiveShareBridge } =
            await import('@annoteer/platform-adapter/TeamsLiveShareBridge');

          // LiveShareHost connects to the real Azure Fluid Relay inside a Teams meeting.
          // This only works when running inside a real Teams meeting iframe.
          const host = teamsJs.LiveShareHost.create();
          bridge = new TeamsLiveShareBridge(host);
        } catch (teamsErr) {
          console.warn('Teams LiveShareHost failed, trying TestLiveShareHost:', teamsErr);
          try {
            // TestLiveShareHost uses a local Fluid test server — works for dev
            // when a meetingId is present but we're not in a real Teams meeting.
            const { TestLiveShareHost } = await import('@microsoft/live-share');
            const { TeamsLiveShareBridge } =
              await import('@annoteer/platform-adapter/TeamsLiveShareBridge');
            const host = TestLiveShareHost.create();
            bridge = new TeamsLiveShareBridge(host);
          } catch (testErr) {
            console.warn('TestLiveShareHost also failed, using DevLiveShareBridge:', testErr);
            bridge = new DevLiveShareBridge();
          }
        }
      } else {
        // Dev mode — no meeting context, use lightweight in-memory fallback
        bridge = new DevLiveShareBridge();
      }

      if (disposed) {
        bridge.dispose();
        return;
      }

      bridgeRef.current = bridge;

      const initialViewState: ViewState = { documentId: null, currentPage: 1 };
      await bridge.initialize(initialViewState);

      if (disposed) {
        bridge.dispose();
        return;
      }

      // Set initial state
      setFollowModeState(bridge.state);

      // Subscribe to state changes.
      //
      // eventCount is a diagnostic, and it exists to answer one question that
      // nothing else can: when the presenter starts presenting, does this
      // client hear about it AT ALL? Reported three times from a phone that
      // was added BEFORE the presenter pressed Present, showed nothing, and
      // then saw the document immediately after being removed and re-added.
      // Re-adding is a fresh initialize, which reads the presenter's state
      // straight off the container -- so the seed path demonstrably works and
      // the fault is in the live path, for a client that was already
      // connected. A count of 0 after Present says the event never arrived
      // and this is transport, not follow logic; a count above 0 with no
      // presenter in it says the opposite.
      unsubscribe = bridge.onStateChanged((state) => {
        if (!disposed) {
          setEventCount((n) => n + 1);
          setFollowModeState(state);
        }
      });

      setIsReady(true);
    }

    init().catch((err) => {
      console.warn('Live Share initialization failed, falling back to dev bridge:', err);
      // Always fall back to dev bridge, even if the original bridge was partially set up.
      if (disposed) return;
      // Clean up any partially-initialized bridge first
      if (bridgeRef.current) {
        try {
          bridgeRef.current.dispose();
        } catch {
          // Ignore — we're already in an error path
        }
        bridgeRef.current = null;
      }
      const fallback = new DevLiveShareBridge();
      bridgeRef.current = fallback;
      fallback
        .initialize({ documentId: null, currentPage: 1 })
        .then(() => {
          if (disposed) return;
          setFollowModeState(fallback.state);
          // Subscribe here too. Until 2026-09-19 only the happy path above
          // did, so on this path the bridge emitted into zero listeners:
          // pressing Present called startPresenting(), the bridge flipped its
          // own state, and React never heard about it -- the button did
          // nothing, silently, every time. Inside Teams the happy path runs,
          // so it did not reach users; but it is why presenting could never
          // be exercised in local dev, which is exactly where the presenting
          // bugs of the last month needed exercising.
          unsubscribe = fallback.onStateChanged((state) => {
            if (!disposed) {
              setEventCount((n) => n + 1);
              setFollowModeState(state);
            }
          });
          setIsReady(true);
        })
        .catch((fallbackErr) => {
          console.error('Dev Live Share fallback also failed:', fallbackErr);
          // Mark as ready anyway so the rest of the app doesn't hang
          if (!disposed) setIsReady(true);
        });
    });

    return () => {
      disposed = true;
      unsubscribe?.();
      if (bridgeRef.current) {
        bridgeRef.current.dispose();
        bridgeRef.current = null;
      }
      setIsReady(false);
      setFollowModeState(null);
    };
  }, [context]);

  const startPresenting = useCallback(async () => {
    if (bridgeRef.current) await bridgeRef.current.startPresenting();
  }, []);

  const stopPresenting = useCallback(async () => {
    if (bridgeRef.current) await bridgeRef.current.stopPresenting();
  }, []);

  const updateViewState = useCallback(async (state: ViewState) => {
    if (bridgeRef.current) await bridgeRef.current.updateViewState(state);
  }, []);

  const beginSuspension = useCallback(async () => {
    if (bridgeRef.current) await bridgeRef.current.beginSuspension();
  }, []);

  const endSuspension = useCallback(async () => {
    if (bridgeRef.current) await bridgeRef.current.endSuspension();
  }, []);

  // Derive convenience booleans from state
  const isPresenting = followModeState?.type === 'activePresenter';
  const isFollowing = followModeState?.type === 'followPresenter';
  const isSuspended = followModeState?.type === 'suspendFollowPresenter';

  const value: LiveShareContextValue = {
    isReady,
    followModeState,
    isPresenting,
    isFollowing,
    isSuspended,
    eventCount,
    startPresenting: isReady ? startPresenting : noop,
    stopPresenting: isReady ? stopPresenting : noop,
    updateViewState: isReady ? updateViewState : noop,
    beginSuspension: isReady ? beginSuspension : noop,
    endSuspension: isReady ? endSuspension : noop,
  };

  return <LiveShareCtx.Provider value={value}>{children}</LiveShareCtx.Provider>;
}

/**
 * Hook to access Live Share meeting state.
 * Must be used within LiveShareProvider.
 */
export function useMeetingState(): LiveShareContextValue {
  const ctx = useContext(LiveShareCtx);
  if (!ctx) {
    throw new Error('useMeetingState must be used within LiveShareProvider');
  }
  return ctx;
}
